// src/components/ApproachSection.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

const tabData = [
  {
    key: "web",
    title: "Web & Software Development",
    shortDescription:
      "We craft robust, scalable, and interactive web and software solutions tailored to client needs, blending creativity with technical precision.",
    points: [
      {
        heading: "Custom Web Development",
        text: "Building responsive, feature-rich websites and web applications for businesses, educational institutes, and startups.",
      },
      {
        heading: "Software Solutions",
        text: "Developing desktop and cloud-based software to streamline operations and automate processes.",
      },
      {
        heading: "UI/UX Excellence",
        text: "Designing intuitive user interfaces and experiences that engage and retain users.",
      },
    ],
    imageSrc:
      "https://images.pexels.com/photos/7988079/pexels-photo-7988079.jpeg",
  },
  {
    key: "ai",
    title: "AI & Automation",
    shortDescription:
      "Leveraging artificial intelligence and automation tools to enhance productivity, decision-making, and operational efficiency.",
    points: [
      {
        heading: "AI Assistants",
        text: "Building AI-powered assistants and chatbots to automate customer support and internal workflows.",
      },
      {
        heading: "Process Automation",
        text: "Streamlining repetitive business tasks with intelligent automation for faster and error-free results.",
      },
      {
        heading: "Data Insights",
        text: "Transforming raw data into actionable insights using AI algorithms and analytics dashboards.",
      },
    ],
    imageSrc:
      "https://media.istockphoto.com/id/2127243626/vector/isometric-smartphone-with-digital-artificial-intelligence-chipset-on-circuit-board-background.jpg?s=612x612&w=0&k=20&c=ajPJGcYJJGMLkVXiKtganIqgZEaRGOvpEoD4jKnWNyw=",
  },
  {
    key: "consulting",
    title: "Consulting & Strategy",
    shortDescription:
      "Providing expert guidance in technology adoption, digital transformation, and project planning to ensure sustainable growth.",
    points: [
      {
        heading: "Project Planning",
        text: "Assisting clients in planning, prioritizing, and executing software and web development projects efficiently.",
      },
      {
        heading: "Digital Transformation",
        text: "Helping businesses adapt to modern technology trends to stay competitive in a fast-evolving digital landscape.",
      },
      {
        heading: "Scalable Solutions",
        text: "Ensuring solutions are scalable, maintainable, and optimized for long-term business growth.",
      },
    ],
    imageSrc:
      "https://images.pexels.com/photos/4098369/pexels-photo-4098369.jpeg",
  },
];

const ApproachSection = () => {
  const [activeTab, setActiveTab] = useState(tabData[0].key);
  const activeContent =
    tabData.find((tab) => tab.key === activeTab) || tabData[0];

  const navigate = useNavigate();

  return (
    <section className="relative bg-[#f9fafb] font-sans overflow-hidden">
      {/* Hero Banner */}
      <div className="relative w-full min-h-[500px] md:min-h-[550px] lg:min-h-[600px] overflow-hidden rounded-none md:rounded-xl max-w-7xl mx-auto mt-10 flex items-center">
        <img
          src="https://images.pexels.com/photos/4427431/pexels-photo-4427431.jpeg"
          alt="Riff Raff Tech Solutions Team"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-black/60"></div>

        <div className="relative z-10 max-w-5xl mx-auto px-6 py-16 md:py-20 lg:py-24 text-center md:text-left">
          <span className="text-sm font-medium text-white bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full inline-block mb-4">
            Our Approach
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif text-white leading-snug">
            Innovative, scalable, and tailored solutions for every client
          </h2>
          <p className="text-sm sm:text-base md:text-lg text-gray-200 mt-4 max-w-3xl mx-auto md:mx-0">
            Riff Raff Tech Solutions is a service and product-based company
            delivering web and software development, AI tools, and strategic
            consulting services. We empower businesses, startups, and
            educational institutes to achieve digital excellence.
          </p>
        </div>
      </div>

      {/* Content Section */}
      <div className="relative -mt-10 sm:-mt-16 md:-mt-20 z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white p-6 sm:p-10 rounded-2xl shadow-xl border border-gray-100">
          {/* Tabs */}
          <div className="flex justify-between flex-wrap border-b border-gray-200 overflow-x-auto">
            {tabData.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`py-4 px-6 text-base sm:text-lg font-medium transition-all duration-300 ${
                  activeTab === tab.key
                    ? "text-gray-900 border-b-2 border-gray-900"
                    : "text-gray-500 hover:text-gray-700 border-b-2 border-transparent"
                }`}
              >
                {tab.title}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 mt-8">
            {/* Left Side */}
            <div className="lg:col-span-2">
              <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
                {activeContent.shortDescription}
              </p>

              <div className="mt-8 space-y-8">
                {activeContent.points.map((point, index) => (
                  <div
                    key={index}
                    className="border-l-2 border-gray-200 pl-6 hover:border-gray-400 transition"
                  >
                    <h4 className="text-lg sm:text-xl font-semibold text-gray-900 font-serif">
                      {point.heading}
                    </h4>
                    <p className="text-gray-600 mt-2 text-sm sm:text-base">
                      {point.text}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Side Image + CTA */}
            <div className="relative w-full h-[300px] sm:h-[400px] md:h-[500px] rounded-xl overflow-hidden shadow-md">
              <img
                src={activeContent.imageSrc}
                alt={activeContent.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-5 left-1/2 transform -translate-x-1/2 w-[90%]">
                <button
                  onClick={() => {
                    navigate("/#contact");
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  className="flex items-center justify-center space-x-2 w-full py-3 sm:py-4 bg-white text-black text-sm sm:text-base font-medium rounded-lg hover:bg-black hover:border-white hover:text-white hover:border-2 transition-all duration-300"
                >
                  <span>Book a free consultation</span>
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M14 5l7 7m0 0l-7 7m7-7H3"
                    ></path>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ApproachSection;
